import { useId } from 'react'
import { ArrowRight, BookOpen, Headphones, PlayCircle } from 'lucide-react'
import { useMediaItems, type MediaItem } from '@/hooks/useMediaItems'
import type { MediaType } from '@/types/database'
import { Picture } from '@/components/ui/Picture'
import { imageNameFromUrl } from '@/lib/images'

const META: Record<MediaType, { title: string; verb: string; icon: typeof BookOpen }> = {
  read: { title: 'Read', verb: 'Read article', icon: BookOpen },
  watch: { title: 'Watch', verb: 'Watch video', icon: PlayCircle },
  listen: { title: 'Listen', verb: 'Listen', icon: Headphones },
}

export function Media() {
  const headingId = useId()
  return (
    <section
      id="media"
      aria-labelledby={headingId}
      className="bg-surface py-section-y"
    >
      <div className="mx-auto max-w-[88rem] px-6 sm:px-10">
        <header className="grid grid-cols-1 gap-y-6 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <span className="eyebrow">Learning Hub</span>
            <h2
              id={headingId}
              className="mt-4 font-display text-display-2 font-extrabold text-ink-deep"
            >
              Read, watch, listen.
            </h2>
          </div>
          <p className="text-base leading-relaxed text-ink-slate lg:col-span-5 lg:col-start-8 lg:pt-3 lg:text-lg">
            Stories, interviews, and insights on aging and longevity.
          </p>
        </header>

        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
          {(Object.keys(META) as MediaType[]).map((type) => (
            <Column key={type} type={type} />
          ))}
        </div>
      </div>
    </section>
  )
}

function Column({ type }: { type: MediaType }) {
  const meta = META[type]
  // First item gets the full card; the rest render as a growing list of
  // clickable links per Website Review ("continually add content").
  const { items, loading, error } = useMediaItems({ type, limit: 5 })
  const Icon = meta.icon
  const [featured, ...rest] = items
  return (
    <div>
      <h3 className="flex items-baseline gap-2 border-b-2 border-ink-deep pb-2">
        <Icon className="h-4 w-4 text-ink-deep" aria-hidden />
        <span className="font-display text-base font-extrabold uppercase tracking-[0.06em] text-ink-deep">
          {meta.title}
        </span>
      </h3>

      <div className="mt-6">
        {error && (
          <p role="alert" className="text-sm text-ink-slate">
            Could not load {meta.title.toLowerCase()} items.
          </p>
        )}
        {loading ? (
          <div
            className="aspect-[4/3] animate-pulse bg-surface-alt"
            aria-hidden
          />
        ) : !featured ? (
          <p className="text-sm leading-relaxed text-ink-slate">
            Coming soon as content is published.
          </p>
        ) : (
          <>
            <MediaCard item={featured} verb={meta.verb} />
            {rest.length > 0 && (
              <ul role="list" className="mt-6 divide-y divide-hairline border-t border-hairline">
                {rest.map((item) => (
                  <li key={item.id}>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-3"
                    >
                      <span className="text-sm font-bold leading-snug text-ink-deep group-hover:text-brand">
                        {item.title}
                      </span>
                      {item.source && (
                        <span className="shrink-0 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-ink-faint">
                          {item.source}
                        </span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const THUMB_CLASS =
  'h-full w-full object-cover transition-transform duration-500 ease-out-quart group-hover:scale-105'


function MediaCard({ item, verb }: { item: MediaItem; verb: string }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-ink-deep/95">
        {imageNameFromUrl(item.thumbnail_url) ? (
          <Picture
            name={imageNameFromUrl(item.thumbnail_url)!}
            sizes="(min-width: 768px) 30vw, 100vw"
            alt=""
            className={THUMB_CLASS}
          />
        ) : item.thumbnail_url ? (
          <img
            src={item.thumbnail_url}
            alt=""
            width={480}
            height={360}
            loading="lazy"
            decoding="async"
            className={THUMB_CLASS}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-ink-deep">
            <span
              aria-hidden
              className="font-display text-7xl font-extrabold text-surface/15"
            >
              {item.source?.[0] ?? '·'}
            </span>
          </div>
        )}
        {item.source && (
          <span className="absolute left-3 top-3 bg-surface px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-ink-deep">
            {item.source}
          </span>
        )}
      </div>
      <h4 className="mt-4 font-display text-xl font-extrabold leading-snug text-ink-deep transition-colors duration-200 ease-out-quart group-hover:text-brand">
        {item.title}
      </h4>
      {item.description && (
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-ink-slate">
          {item.description}
        </p>
      )}
      <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.08em] text-ink-deep">
        {verb}
        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 ease-out-quart group-hover:translate-x-1" aria-hidden />
        <span className="sr-only">(opens in a new tab)</span>
      </span>
    </a>
  )
}
